import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { createUser } from "api/users";
import {
  Button,
  notify,
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
} from "Components/ui";
import { useAuth } from "hooks/useAuth";
import UserFields, {
  addUserSchema,
  EMPTY_USER_FORM,
  type UserFormValues,
} from "./Components/UserFields";
import { useRoleOptions } from "./data";

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

/** Side panel that creates a user (POST /api/rbac/users). */
const AddUserModal: React.FC<AddUserModalProps> = ({ open, onClose, onSaved }) => {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("userManagement", "write");
  const roles = useRoleOptions(open && canCreate);

  const form = useForm<UserFormValues>({
    resolver: zodResolver(addUserSchema),
    defaultValues: EMPTY_USER_FORM,
  });
  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = form;

  React.useEffect(() => {
    if (open) {
      reset(EMPTY_USER_FORM);
    }
  }, [open, reset]);

  const onSubmit = async (values: UserFormValues) => {
    if (!canCreate) {
      notify.error("You don't have permission to add users.");
      return;
    }
    try {
      await createUser({
        name: values.name,
        userName: values.userName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        password: values.password,
        role: values.role,
      });
      notify.success(`${values.name} added`);
      onSaved();
    } catch (err) {
      notify.error(err, { fallback: "The user wasn't created." });
    }
  };

  return (
    <Sheet
      open={open && canCreate}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) {
          onClose();
        }
      }}
    >
      <SheetContent>
        <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
          <SheetHeader
            title="Add user"
            description="They sign in with their username and password, then a code sent to WhatsApp."
          />
          <SheetBody>
            <UserFields form={form} mode="add" roles={roles} />
          </SheetBody>
          <SheetFooter>
            <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              Add user
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default AddUserModal;
